import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Worker } from '../lib/supabase';
import { WorkerForm } from '../components/forms/WorkerForm';
import { Plus, CreditCard as Edit, Trash2, Users, Shield, AlertTriangle, Eye, UserCheck, Award } from 'lucide-react';

export const WorkersPage: React.FC = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWorkers(data || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (workerData: Partial<Worker>) => {
    setSubmitting(true);
    try {
      if (editingWorker) {
        const { error } = await supabase
          .from('workers')
          .update(workerData)
          .eq('id', editingWorker.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('workers')
          .insert([workerData]);

        if (error) throw error;
      }

      setShowModal(false);
      setEditingWorker(null);
      fetchWorkers();
    } catch (error) {
      console.error('Error saving worker:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (workerId: string) => {
    if (confirm('Are you sure you want to delete this worker? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('workers')
          .delete()
          .eq('id', workerId);

        if (error) throw error;
        fetchWorkers();
      } catch (error) {
        console.error('Error deleting worker:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const isVisitorRole = (role: string) => role?.toLowerCase() === 'visitor';
  const regularWorkers = workers.filter(w => !isVisitorRole(w.role));
  const visitors = workers.filter(w => isVisitorRole(w.role));

  const WorkerTable = ({ list, isVisitor = false }: { list: Worker[]; isVisitor?: boolean }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {isVisitor ? 'Visitor' : 'Worker'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {isVisitor ? 'Contact' : 'Company'}
            </th>
            {!isVisitor && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Certification
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {list.map((worker) => (
            <tr key={worker.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isVisitor ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    {isVisitor
                      ? <UserCheck className="h-5 w-5 text-purple-600" />
                      : <Users className="h-5 w-5 text-blue-600" />
                    }
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{worker.name}</div>
                    <div className="text-sm text-gray-500">{worker.email}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 capitalize">{worker.role.replace('_', ' ')}</div>
                <div className="text-sm text-gray-500">{worker.phone}</div>
                {!isVisitor && worker.hourly_pay_rate && (
                  <div className="text-sm text-green-600 font-medium">${worker.hourly_pay_rate.toFixed(2)}/hr</div>
                )}
                {!isVisitor && worker.overtime_pay_rate && (
                  <div className="text-xs text-orange-600 font-medium">OT: ${worker.overtime_pay_rate.toFixed(2)}/hr</div>
                )}
              </td>
              {!isVisitor && (
                <td className="px-6 py-4 whitespace-nowrap">
                  {worker.certifications && worker.certifications.length > 0 ? (
                    <div className="space-y-1">
                      {worker.certifications.slice(0, 2).map((cert, index) => (
                        <div key={index} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {cert}
                        </div>
                      ))}
                      {worker.certifications.length > 2 && (
                        <div className="text-xs text-gray-500">+{worker.certifications.length - 2} more</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">No certifications</div>
                  )}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap">
                {worker.status === 'active' ? (
                  <div className="flex items-center space-x-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-green-600">Active</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-xs text-red-600">Inactive</span>
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex space-x-2">
                  <Link
                    to={`/workers/${worker.id}`}
                    className="text-blue-600 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => { setEditingWorker(worker); setShowModal(true); }}
                    className="text-blue-600 hover:text-blue-900"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(worker.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Worker Management</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/workers/training"
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Award className="h-5 w-5 text-orange-600" />
            <span>Training &amp; Certificates</span>
          </Link>
          <button
            onClick={() => { setEditingWorker(null); setShowModal(true); }}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add New Worker</span>
          </button>
        </div>
      </div>

      {/* Registered Workers */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Registered Workers</h3>
          </div>
          <span className="text-sm text-gray-500">{regularWorkers.length} {regularWorkers.length === 1 ? 'worker' : 'workers'}</span>
        </div>
        {regularWorkers.length > 0 ? (
          <WorkerTable list={regularWorkers} />
        ) : (
          <div className="text-center py-10">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No workers yet.</p>
            <button
              onClick={() => { setEditingWorker(null); setShowModal(true); }}
              className="mt-3 text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Add the first worker
            </button>
          </div>
        )}
      </div>

      {/* Visitors */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-medium text-gray-900">Visitors</h3>
          </div>
          <span className="text-sm text-gray-500">{visitors.length} {visitors.length === 1 ? 'visitor' : 'visitors'}</span>
        </div>
        {visitors.length > 0 ? (
          <WorkerTable list={visitors} isVisitor />
        ) : (
          <div className="text-center py-10">
            <UserCheck className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No visitors on record.</p>
          </div>
        )}
      </div>

      {showModal && (
        <WorkerForm
          worker={editingWorker}
          onSubmit={handleSubmit}
          onClose={() => { setShowModal(false); setEditingWorker(null); }}
          loading={submitting}
        />
      )}
    </div>
  );
};